import { useEffect, useRef, useState } from 'react';
import { Alert, Button } from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { useApi } from '@/lib/useApi';

interface ItemRobo {
  id: string;
  reservaId: string;
  localizador: string;
  companhia: string;
  resultado: string;
  statusSite: string | null;
  horarioSite: string | null;
  conferido: boolean;
  erro: string | null;
  temEvidencia: boolean;
  createdAt: string;
}

interface ExecucaoRobo {
  id: string;
  status: 'EM_ANDAMENTO' | 'CONCLUIDA' | 'INTERROMPIDA_CAPTCHA' | 'FALHOU';
  total: number;
  processadas: number;
  conferidas: number;
  falhas: number;
  mensagem: string | null;
  iniciadaPor: string;
  createdAt: string;
  finalizadaEm: string | null;
  itens: ItemRobo[];
}

interface SituacaoRobo {
  ativo: boolean;
  cias: string[];
  aConferir: number;
  ultima: ExecucaoRobo | null;
}

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';
const INTERVALO_MS = 3_000;
const fmtHora = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Bahia',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

function resumoFinal(
  ex: ExecucaoRobo,
): { kind: 'success' | 'warning' | 'error'; texto: string } | null {
  if (ex.status === 'CONCLUIDA') {
    return {
      kind: ex.falhas ? 'warning' : 'success',
      texto: `Robô terminou: ${ex.conferidas} reserva(s) conferida(s)${ex.falhas ? `, ${ex.falhas} para conferir à mão (veja abaixo)` : ''}.`,
    };
  }
  if (ex.status === 'INTERROMPIDA_CAPTCHA')
    return {
      kind: 'warning',
      texto: ex.mensagem ?? 'O site pediu CAPTCHA. Confira as restantes à mão.',
    };
  if (ex.status === 'FALHOU')
    return { kind: 'error', texto: `O robô parou: ${ex.mensagem ?? 'erro desconhecido'}` };
  return null;
}

/**
 * Painel da gestão: dispara o robô que confere as reservas no site da
 * companhia e mostra o que ele não conseguiu conferir (com captura de tela).
 * As conferências feitas pelo robô aparecem na lista abaixo como as da equipe.
 */
export function RoboConferencia({ dias, onAtualizou }: { dias: number; onAtualizou: () => void }) {
  const { data, error, reload } = useApi<SituacaoRobo>(`/crm/conferencia/robo?dias=${dias}`);
  const [execucao, setExecucao] = useState<ExecucaoRobo | null>(null);
  const [acompanhando, setAcompanhando] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [verItens, setVerItens] = useState(false);
  // Sempre a versão mais recente do callback, sem reiniciar o acompanhamento.
  const onAtualizouRef = useRef(onAtualizou);
  onAtualizouRef.current = onAtualizou;

  // Ao abrir: mostra o último lote e retoma o acompanhamento se ainda estiver rodando.
  useEffect(() => {
    if (!data?.ultima) return;
    setExecucao((atual) => atual ?? data.ultima);
    if (data.ultima.status === 'EM_ANDAMENTO') setAcompanhando(data.ultima.id);
  }, [data]);

  // Progresso: consulta o lote e recarrega a lista de conferência a cada passo.
  useEffect(() => {
    if (!acompanhando) return;
    let cancelado = false;
    let ultimoProcessado = -1;
    const passo = async () => {
      try {
        const ex = await api.get<ExecucaoRobo>(`/crm/conferencia/robo/execucoes/${acompanhando}`);
        if (cancelado) return;
        setExecucao(ex);
        if (ex.processadas !== ultimoProcessado) {
          ultimoProcessado = ex.processadas;
          onAtualizouRef.current();
        }
        if (ex.status !== 'EM_ANDAMENTO') {
          setAcompanhando(null);
          setVerItens(ex.falhas > 0);
          void reload();
        }
      } catch (e) {
        if (!cancelado) setErro(errorMessage(e));
      }
    };
    void passo();
    const t = window.setInterval(() => void passo(), INTERVALO_MS);
    return () => {
      cancelado = true;
      window.clearInterval(t);
    };
  }, [acompanhando, reload]);

  async function disparar() {
    setErro(null);
    setEnviando(true);
    try {
      const r = await api.post<{ execucaoId: string; total: number }>(
        `/crm/conferencia/robo?dias=${dias}`,
      );
      setExecucao(null);
      setVerItens(false);
      setAcompanhando(r.execucaoId);
    } catch (e) {
      const emAndamento =
        e instanceof ApiError && e.code === 'ROBO_EM_ANDAMENTO'
          ? (e.details as { execucaoId?: string } | undefined)?.execucaoId
          : undefined;
      if (emAndamento) setAcompanhando(emAndamento);
      else setErro(errorMessage(e));
    } finally {
      setEnviando(false);
    }
  }

  if (error) return <Alert>{error}</Alert>;
  if (!data) return null;

  if (!data.ativo) {
    return (
      <section className="card robo">
        <strong>Robô de conferência desligado</strong>
        <p className="muted small">
          Para ligar, valide os seletores da companhia e defina ROBO_CONFERENCIA_CIAS na Render
          (ex.: LA,G3). Até lá, a conferência continua manual.
        </p>
      </section>
    );
  }

  const rodando = acompanhando !== null;
  const pct = execucao?.total ? Math.round((execucao.processadas / execucao.total) * 100) : 0;
  const final = execucao && !rodando ? resumoFinal(execucao) : null;
  const paraConferirAMao = execucao?.itens.filter((i) => !i.conferido) ?? [];

  return (
    <section className="card robo">
      <div className="row between">
        <div>
          <strong>Robô de conferência</strong>
          <p className="muted small">
            Confere no site da companhia ({data.cias.join(', ')}) as reservas que faltam hoje e
            registra o resultado aqui, como &quot;Robô&quot;.
            {!rodando && ` ${data.aConferir} reserva(s) na fila.`}
          </p>
        </div>
        <Button
          onClick={() => void disparar()}
          loading={enviando}
          disabled={rodando || data.aConferir === 0}
        >
          {rodando ? 'Conferindo…' : 'Conferir com o robô'}
        </Button>
      </div>

      {erro && <Alert>{erro}</Alert>}
      {final && <Alert kind={final.kind}>{final.texto}</Alert>}

      {execucao && rodando && (
        <div aria-live="polite">
          <p className="small">
            {execucao.processadas} de {execucao.total} · {execucao.conferidas} conferida(s) ·{' '}
            {execucao.falhas} para conferir à mão
          </p>
          <div
            className="conf__barra"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            aria-label="Progresso do robô"
          >
            <div style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {execucao && !rodando && paraConferirAMao.length > 0 && (
        <div className="robo__itens">
          <button
            type="button"
            className="btn btn--ghost small"
            aria-expanded={verItens}
            onClick={() => setVerItens((v) => !v)}
          >
            {verItens ? 'Ocultar' : 'Ver'} as {paraConferirAMao.length} que o robô não conseguiu
            conferir
          </button>
          {verItens && (
            <ul className="robo__lista">
              {paraConferirAMao.map((i) => (
                <li key={i.id}>
                  <span className="pill pill--warn">{i.resultado}</span>{' '}
                  <strong>{i.localizador}</strong> <span className="muted">({i.companhia})</span>
                  {i.erro && <div className="muted small robo__erro">{i.erro}</div>}
                  {i.temEvidencia && (
                    <a
                      className="small"
                      href={`${BASE_URL}/crm/conferencia/robo/itens/${i.id}/evidencia`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ver captura de tela
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="muted small">
            Último lote: {fmtHora.format(new Date(execucao.createdAt))}, por {execucao.iniciadaPor}.
          </p>
        </div>
      )}
    </section>
  );
}
